/**
 * Multi-Variant utilities.
 *
 * Product variants (size, color, material, etc.) for product-type listings.
 *
 * Variants stored in listing.publicData.variants = [
 *   {
 *     id: string,
 *     attributes: { size: 'M', color: 'blue' },
 *     priceCents: number | null,  — null = use listing base price
 *     stock: number | null,
 *     sku: string
 *   }
 * ]
 *
 * Variant attribute options stored in listing.publicData.variantOptions = {
 *   size: ['XS', 'S', 'M', 'L', 'XL'],
 *   color: ['red', 'blue', 'green']
 * }
 */

/**
 * Get variants from listing.
 * @param {Object|null} listing
 * @returns {Object[]}
 */
export const getVariants = (listing) =>
  listing?.attributes?.publicData?.variants || [];

/**
 * Get variant attribute options.
 * @param {Object|null} listing
 * @returns {Object}
 */
export const getVariantOptions = (listing) =>
  listing?.attributes?.publicData?.variantOptions || {};

/**
 * Find a specific variant by attribute combination.
 * @param {Object[]} variants
 * @param {Object} attributes — e.g. { size: 'M', color: 'blue' }
 * @returns {Object|null}
 */
export const findVariant = (variants, attributes) => {
  if (!Array.isArray(variants)) return null;
  return variants.find(v => {
    return Object.entries(attributes).every(([key, val]) => v.attributes?.[key] === val);
  }) || null;
};

/**
 * Get the price for a variant (falls back to listing base price).
 * @param {Object|null} variant
 * @param {number} basePriceCents
 * @returns {number}
 */
export const getVariantPrice = (variant, basePriceCents) =>
  variant?.priceCents ?? basePriceCents;

/**
 * Check if a variant is in stock.
 * @param {Object|null} variant
 * @returns {boolean}
 */
export const isVariantInStock = (variant) => {
  if (!variant) return false;
  if (variant.stock === null || variant.stock === undefined) return true;
  return variant.stock > 0;
};

/**
 * Validate variants array.
 * @param {Object[]} variants
 * @returns {string|null}
 */
export const validateVariants = (variants) => {
  if (!Array.isArray(variants)) return 'Variants must be an array';
  for (const v of variants) {
    if (!v.id) return 'Each variant must have an id';
    if (!v.attributes || typeof v.attributes !== 'object') return 'Each variant must have attributes';
    if (v.priceCents !== null && v.priceCents !== undefined && v.priceCents < 0) {
      return 'Variant price cannot be negative';
    }
    if (v.stock !== null && v.stock !== undefined && v.stock < 0) {
      return 'Variant stock cannot be negative';
    }
  }
  // Check for duplicate attribute combinations
  const seen = new Set();
  for (const v of variants) {
    const key = JSON.stringify(v.attributes);
    if (seen.has(key)) return 'Duplicate variant attribute combination';
    seen.add(key);
  }
  return null;
};
